import { useParams } from 'react-router-dom'
import { Stack, Tabs } from '@mantine/core'
import { PageContainer } from '@shared/components/app-shell/page-container/page-container'
import { PAGE_TITLES } from '@/constants/page-titles'
import { useContext, useEffect } from 'react'
import { useNavigation } from '@/hooks/use-navigation'
import { ReceivalHistory } from '@/pages/wool-data/receival-history/receival-history.page'
import { WoolContracts } from '@/pages/wool-data/wool-contracts/wool-contracts.page'
import { LotDetails } from '@/pages/wool-data/lot-details/lot-details.page'
import { CreditorHistory } from '@/pages/wool-data/creditor-history/creditor-history.page'
import { ClipSummary } from '@/pages/wool-data/clip-summary/clip-summary.page'
import { StationContext } from '@/pages/station-context'
import { StationAuthRole } from '@shared/grower-api/.generated/types'
import { BaleDetails } from '@/pages/wool-data/bale-details/bale-details.page'

export const WoolDataTabValue = {
  CLIP_SUMMARY: 'clip-summary',
  WOOL_CONTRACTS: 'wool-contracts',
  LOT_DATA: 'lot-data',
  BALE_DETAILS: 'bale-details',
  CREDITOR_HISTORY: 'creditor-history',
  RECEIVAL_HISTORY: 'receival-history',
}

export function WoolDataTabsPage() {
  const { navigateToWoolDataTab } = useNavigation()
  const { stationRecordNumber, woolDataTabValue } = useParams()

  const { station } = useContext(StationContext)
  const isFullAccess = station.roles.includes(StationAuthRole.FullAccess)
  const defaultTab = isFullAccess ? WoolDataTabValue.CLIP_SUMMARY : WoolDataTabValue.WOOL_CONTRACTS

  // If root wool data route, redirect to default tab
  useEffect(() => {
    if (!stationRecordNumber) return

    if (!isFullAccess && woolDataTabValue === WoolDataTabValue.CLIP_SUMMARY) {
      navigateToWoolDataTab(station.recordNumber, WoolDataTabValue.WOOL_CONTRACTS)
    }
    if (woolDataTabValue === undefined) {
      navigateToWoolDataTab(stationRecordNumber, defaultTab)
    }
  }, [woolDataTabValue, stationRecordNumber, isFullAccess, defaultTab])

  return (
    <PageContainer title={PAGE_TITLES.WOOL_DATA}>
      <Stack data-testid='wool-data'>
        <Tabs
          keepMounted={false}
          defaultValue={defaultTab}
          value={woolDataTabValue}
          onChange={(value) => navigateToWoolDataTab(stationRecordNumber ?? '', value ?? '')}
        >
          <Tabs.List mb='md'>
            <Tabs.Tab value={WoolDataTabValue.CLIP_SUMMARY} disabled={!isFullAccess}>
              Clip Summary
            </Tabs.Tab>
            <Tabs.Tab value={WoolDataTabValue.WOOL_CONTRACTS}>Wool Contracts</Tabs.Tab>
            <Tabs.Tab value={WoolDataTabValue.LOT_DATA}>Lot Data</Tabs.Tab>
            <Tabs.Tab value={WoolDataTabValue.BALE_DETAILS}>Bale Details</Tabs.Tab>
            <Tabs.Tab value={WoolDataTabValue.CREDITOR_HISTORY}>Creditor History</Tabs.Tab>
            <Tabs.Tab value={WoolDataTabValue.RECEIVAL_HISTORY}>Receival History</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value={WoolDataTabValue.CLIP_SUMMARY}>
            <ClipSummary />
          </Tabs.Panel>
          <Tabs.Panel value={WoolDataTabValue.WOOL_CONTRACTS}>
            <WoolContracts />
          </Tabs.Panel>
          <Tabs.Panel value={WoolDataTabValue.LOT_DATA}>
            <LotDetails />
          </Tabs.Panel>
          <Tabs.Panel value={WoolDataTabValue.BALE_DETAILS}>
            <BaleDetails />
          </Tabs.Panel>
          <Tabs.Panel value={WoolDataTabValue.CREDITOR_HISTORY}>
            <CreditorHistory />
          </Tabs.Panel>
          <Tabs.Panel value={WoolDataTabValue.RECEIVAL_HISTORY}>
            <ReceivalHistory />
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </PageContainer>
  )
}
